import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { AlertProps } from '@Components/Alert';
import CommonConstants from '@Constants/common.constants';
import { BaseAlertProps, CommonApiError } from '@Typings/global.types';

/**
 * useAlert Hook
 *
 * @returns object containing triggerAlert function
 */
const useAlert = (): {
  /**
   * Opens the alert - error or success based on the message type
   * @param message - Alert Message or Error Object
   * @param setAlertState - Function to set the state of Alert
   * @param severity - (optional) Severity of the alert
   * @param isMessageInterpolated - (optional) Whether the message is interpolated or not (translation key or normal string)
   */
  triggerAlert: (
    message: string | FetchBaseQueryError | SerializedError,
    setAlertState: Dispatch<SetStateAction<BaseAlertProps>>,
    severity?: AlertProps['severity'],
    isMessageInterpolated?: boolean
  ) => void;
} => {
  const { t } = useTranslation();

  /**
   * Helps to get the Error Message according to the error
   * @param error - The error object
   * @returns The error message
   */
  const getApiErrorMessage = useCallback(
    (error: FetchBaseQueryError | SerializedError): string => {
      const errorObj =
        error && 'data' in error
          ? { ...(error.data as CommonApiError) }
          : {
              message: CommonConstants.GLOBAL_MESSAGES.GENERIC_ERROR_MESSAGE,
            };
      // if there is an server error, then display generic error message
      if (error && 'status' in error && error.status === 500) {
        errorObj.message =
          CommonConstants.GLOBAL_MESSAGES.GENERIC_ERROR_MESSAGE;
      }
      return errorObj.message;
    },
    []
  );

  /**
   * Helps to filter out FetchBaseQueryError as these are handled by global error middleware
   * This is to avoid showing 2 error alerts for the same error
   * @param error The error object thrown by API
   * @returns whether or not to display the error alert
   */
  const shouldDisplayAlert = useCallback(
    (error: FetchBaseQueryError | SerializedError): boolean => {
      let shouldDisplay = true;
      const globalErrors = [
        CommonConstants.GLOBAL_ERROR_CODES.FETCH_ERROR,
        CommonConstants.GLOBAL_ERROR_CODES.PARSING_ERROR,
      ];
      if (error && 'status' in error) {
        if (
          typeof error.status === 'string' &&
          globalErrors.includes(error.status)
        ) {
          shouldDisplay = false;
        }
      }
      return shouldDisplay;
    },
    []
  );

  /**
   * Opens the alert - error or success based on the message type
   * @param message - Alert Message or Error Object
   * @param setAlertState - Function to set the state of Alert
   * @param severity - (optional) Severity of the alert
   * @param isMessageInterpolated - (optional) Whether the message is interpolated or not (translation key or normal string)
   */
  const triggerAlert = useCallback(
    (
      message: string | FetchBaseQueryError | SerializedError,
      setAlertState: Dispatch<SetStateAction<BaseAlertProps>>,
      severity?: AlertProps['severity'],
      isMessageInterpolated?: boolean
    ) =>
      typeof message === 'string'
        ? setAlertState({
            openAlert: true,
            message: isMessageInterpolated
              ? message
              : t(`alertMessages.${message}`),
            severity: severity || 'success',
            resetAlert: setAlertState,
          })
        : setAlertState({
            openAlert: shouldDisplayAlert(message),
            message: t(`alertMessages.${getApiErrorMessage(message)}`),
            severity: 'error',
            resetAlert: setAlertState,
          }),
    [t, getApiErrorMessage, shouldDisplayAlert]
  );

  return { triggerAlert };
};

export default useAlert;
